import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {SelfAlignedBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {LayoutUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {Loader} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/loader/Loader';
import {IViewportInfo} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/Viewport';
import {IRepeatedData, VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {LayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModel';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {LoaderModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/loader/LoaderModel';
import {OpacityModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/opacity/OpacityModel';
import {PerspectiveModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/perspective/PerspectiveModel';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RepeaterDisplayType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterDisplayType';
import {
    DEFAULT_TILES_NUMBER_OF_COUNT,
    RepeaterModel
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModel';
import {RotationModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationModel';
import {ScaleModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scale/ScaleModel';
import {SkewModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/skew/SkewModel';
import {
    MouseOverRef,
    SelectionInteractionMode,
    SurfaceBounds
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {IBoxMoveState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {DnDMask} from '@mintdata-wsp/common/view/surface/DnDMask';
import {AString} from '@mintdata-wsp/mint-kit/api/values/AString';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BoxDecorator} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxDecorator';
import {BoxTitle} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxTitle';
import {BoxViewContent} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxViewContent';
import {ResizeHandles} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/ResizeHandles';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes,
    IComponentStateContext
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {UnitsType} from '@mintdata/mint-kit/api/models/UnitsType';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {ObjectUtils} from '@mintdata/mint-kit/impl/util/ObjectUtils';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, untracked} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {OtherUserSelection} from './OtherUserSelection';

const styles = require('./BoxView.less');
const cx = classnames.bind(styles);

export interface IBoxViewProps
{
    visualBox:VisualBox;
    repeater:Repeater;
    isInOverlays?:boolean;
    isHiddenOverlay?:boolean;
    isReactive?:boolean;
}

export interface IBoxViewState
{
    dragStartX?:number;
    dragStartY?:number;
    dragDeltaX?:number;
    dragDeltaY?:number;

    resizeStartX?:number;
    resizeStartY?:number;
    resizeDeltaX?:number;
    resizeDeltaY?:number;

    resizeStartWidth?:number;
    resizeStartHeight?:number;
    resizeDeltaWidth?:number;
    resizeDeltaHeight?:number;
}

@observer
class BoxView extends BaseComponentWithStateContext<IBoxViewProps, IBoxViewState>
{
    public static readonly childContextTypes = componentStateContextTypes;

    public static defaultProps:Partial<IBoxViewProps> = {
        isReactive: true,
        isInOverlays: false,
        isHiddenOverlay: false
    };

    private _uniqueId:GUID = IdUtil.newComponentId();

    private _refHandlers = {
        box: (ref:HTMLElement | null) =>
        {
            const {visualBox, repeater} = this.props;

            if( ref )
            {
                if( repeater.levels.every(({index}) => index === 0) )
                    controllers.ui.surfaceArea.setBoxRef(visualBox.box, ref);

                controllers.ui.surfaceArea.setRepeatedBoxRef(visualBox.box, repeater, ref);
                commonControllers.animation.registerTransformRef(visualBox, repeater, ref.parentElement);
            }
            else
            {
                controllers.ui.surfaceArea.deleteRepeatedBoxRef(visualBox.box, repeater);
                commonControllers.animation.unregisterTransformRef(visualBox, repeater);
            }
        }
    };

    public constructor(props)
    {
        super(props);

        this.state = {};
    }

    private _updateRepeaterCache(props)
    {
        const {repeater} = props;
        const {ui: {visualDesigner: {surfaceEditor: {area: {repeatersCache}}}}} = rootState;

        repeatersCache.set(this._uniqueId, repeater);
    }

    public shouldComponentUpdate(nextProps:IBoxViewProps, nextState:IBoxViewState, nextContext:IComponentStateContext)
    {
        return !ObjectUtils.shallowCompareKeys(this.props, nextProps) ||
            !ObjectUtils.shallowCompareKeys(this.context, nextContext);
    }

    public componentDidMount():void
    {
        this._updateRepeaterCache(this.props);
    }

    public componentWillReceiveProps(newProps):void
    {
        this._updateRepeaterCache(newProps);
    }

    public componentWillUnmount():void
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {repeatersCache}}}}} = rootState;

        repeatersCache.delete(this._uniqueId);
    }

    public render():JSX.Element
    {
        if( this.props.isReactive )
            return this._render();

        return untracked(() => this._render());
    }

    @bind
    private _render():JSX.Element
    {
        const {surfaceArea} = controllers.ui;

        const {isReactive, visualBox, repeater} = this.props;

        const isBoxVisible:boolean = this._isVisible;

        if( !isBoxVisible )
            return null;

        if( this._isOnReparentingByAnotherUser() && visualBox.parent )
            return null;

        const isInPreviewMode:boolean = surfaceArea.isInPreviewOrZenPreviewMode();
        const isBoxRepeated:boolean = !repeater.isFirstVisible;

        const bounds:SelfAlignedBounds = this._addDeltaIfMovedByAnotherUser(this._getBounds());

        const cursorStyle:React.CSSProperties = this._cursorStyle;
        const rotationStyle:React.CSSProperties = this._boxRotationStyle;

        let style:React.CSSProperties = {
            position: this._isSelfDirected ? this._fixPositionIfMovedByAnotherUser() : 'relative',
            ...bounds,
            ...cursorStyle,
        };

        if( rotationStyle )
            style = {
                ...style,
                ...rotationStyle
            };

        const scaleStyle:React.CSSProperties = this._scale.toCss(style);

        if( scaleStyle )
            style = {
                ...style,
                ...scaleStyle
            };

        const skewStyle:React.CSSProperties = this._skew.toCss(style);

        if( skewStyle )
            style = {
                ...style,
                ...skewStyle
            };

        const perspectiveStyle:React.CSSProperties = this._perspective.toCss(style);

        if( perspectiveStyle )
            style = {
                ...style,
                ...perspectiveStyle
            };

        if( this._opacity.isEnabled.value && this._opacity.opacity.value !== 1 )
            style.opacity = this._opacity.opacity.value;

        const {area: {surfaceDnDState: {dragContext}}} = rootState.ui.visualDesigner.surfaceEditor;

        return (
            <div
                data-box-wrapper-id={visualBox.box.id}
                data-box-wrapper-selected={this._isDataBoxSelected || undefined}
                data-box-wrapper-reparenting={this._isDataBoxReparenting || undefined}
                data-box-wrapper-mouse-over={this._isDataMouseOverBox || undefined}
                data-droppable-box-wrapper-mouse-over={this._isDataMouseOverDroppableBox || undefined}
                className={cx('boxWrapper', {
                    isReparenting: this._isDataBoxReparenting || this._isOnReparentingByAnotherUser(),
                    isSelectedByOtherUsers: !this._isBoxSelected && this._hasOtherUserSelections
                })}
                style={style}
            >
                <div
                    ref={this._refHandlers.box}
                    data-box-id={visualBox.box.id}
                    data-box-selected={this._isDataBoxSelected || undefined}
                    data-box-mouse-over={this._isDataMouseOverBox || undefined}
                    data-box-view="true"
                    data-box-dnd-group={this._dndGroupName || undefined}
                    data-box-dnd-target={this._dndTarget !== 'false' ? this._dndTarget : undefined}
                    data-content-holder="true"
                    data-repeated-box={!isInPreviewMode && isBoxRepeated || undefined}
                    className={cx('boxView', 'contentHolder', {
                        inPreviewMode: isInPreviewMode,
                        inDesignMode: !isInPreviewMode,
                        pointerEventsNone: this._shouldSetPointerEventsNone
                    })}
                    onMouseOver={this._onBoxMouseOver}
                    onMouseLeave={this._onBoxMouseLeave}
                    onMouseDown={this._onBoxMouseDown}
                >
                    <BoxViewContent
                        isReactive={isReactive}
                        visualBox={visualBox}
                        repeater={repeater}
                        isDesignMode={!isInPreviewMode}
                        viewportInfo={this._getViewportInfoProp()}
                        isBoxSelected={this._isBoxSelected || undefined}
                    />

                    <Loader loaderPropValue={this._loader} />
                    {
                        dragContext && dragContext.dnd.isEnabled.value && this._isDropTarget && surfaceArea.isInPreviewOrZenPreviewMode() &&
                        <DnDMask
                            visualBox={visualBox}
                            repeater={repeater}
                            boxUniqueId={this._uniqueId}
                        />
                    }
                </div>
                {
                    !isInPreviewMode && !isBoxRepeated &&
                    this._renderGeometricBoundingBox()
                }
                {
                    !isInPreviewMode && !isBoxRepeated &&
                    this._renderBoxControls()
                }
            </div>
        );
    }

    private _renderBoxControls():JSX.Element
    {
        const {visualBox} = this.props;

        const isBoxSelected:boolean = this._isBoxSelected && !this._areMultipleBoxesSelected;
        const isDataMouseOverBox:boolean = this._isDataMouseOverBox;
        const isViewport:boolean = visualBox.box.isViewport;

        if( !isViewport && !isBoxSelected && !isDataMouseOverBox && !this._hasOtherUserSelections )
            return null;

        return (
            <div
                data-box-controls="true"
                className={cx(
                    'boxControls',
                    {inactive: isViewport && !isBoxSelected}
                )}
                onMouseOver={this._onBoxMouseOver}
                onMouseLeave={this._onBoxMouseLeave}
            >
                {(isViewport || isBoxSelected) && this._renderDecorator()}
                {!this._isOnMoveByAnotherUser() && this._renderResizeHandles()}
                {this._renderOtherUserSelections()}
            </div>
        );
    }

    private _renderGeometricBoundingBox():JSX.Element
    {
        const isSelected:boolean = this._isBoxSelected;

        if( !isSelected ) return null;

        const {visualBox} = this.props;
        const {rotationProp} = visualBox.box.componentDesc;
        const isRotationEnabled = rotationProp.value.isEnabled.value;
        const {surfaceArea} = controllers.ui;
        const areMultipleBoxesSelected:boolean = this._areMultipleBoxesSelected;

        if( !areMultipleBoxesSelected && (!isRotationEnabled || rotationProp.value.angle.value % 360 === 0) )
            return null;

        // const bounds:Bounds = surfaceArea.getBoxesBounds([visualBox.box]);

        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        // TODO: Fix for Layout
        let style:React.CSSProperties = {
            // top: bounds.y,
            // left: bounds.x,
            // width: bounds.width * zoom,
            // height: bounds.height * zoom
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        };

        const rotationStyle:React.CSSProperties = this._boxNegRotationStyle;

        if( zoom === 1 )
        {
            if( rotationStyle )
            {
                style.transformOrigin = rotationStyle.transformOrigin;
                style.transform = rotationStyle.transform;

                style.display = 'none'; // TODO: Fix for Layout
            }
        }

        if( zoom !== 1 )
            style.zoom = 1 / zoom;

        const displayName = surfaceArea.getBoxName(visualBox.box);

        return (
            <div
                data-box-geometry="true"
                style={style}
                className={cx('geometricBoundingBox')}
                onMouseOver={this._onBoxMouseOver}
                onMouseLeave={this._onBoxMouseLeave}
            >
                {
                    !areMultipleBoxesSelected &&
                    <BoxTitle displayName={displayName} />
                }
            </div>
        );
    }

    private _renderResizeHandles()
    {
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const className:string = this._isBoxSelected ? cx('visibleContent') : cx('hiddenContent');

        const {visualBox} = this.props;
        const {componentDesc} = visualBox.box;
        const isBoxLocked:boolean = componentDesc.lockProp.value.value;

        return (
            <ResizeHandles
                className={className}
                onBoxResizeStart={this._onBoxResizeStart}
                onBoxResize={this._onBoxResizeInProgress}
                onBoxResizeStop={this._onBoxResizeStop}
                isSelfDirected={this._isSelfDirected}
                isBoxLocked={isBoxLocked}
                isBoxSelected={this._isBoxSelected}
                zoom={zoom}
            />
        );
    }

    private get _hasOtherUserSelections()
    {
        return this._getOtherUserSelections.length > 0;
    }

    @computed
    private get _getOtherUserSelections()
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        const {id} = this.props.visualBox.box;

        return activeUsers.otherUserSelectionState.get(id) || [];
    }

    @computed
    private get _cursorStyle():React.CSSProperties | null
    {
        const {surfaceArea} = controllers.ui;
        const isInPreviewMode:boolean = surfaceArea.isInPreviewMode();

        if( !isInPreviewMode ) return null;

        const {visualBox} = this.props;

        const componentName = visualBox.box.component.name;

        if( surfaceArea.isComponentWithSelfCursor(componentName) ) return null;

        const {componentDesc} = visualBox.box;
        const {cursorProp} = componentDesc;

        const isEnabled = cursorProp.value.isEnabled.value;

        return isEnabled ? {cursor: cursorProp.value.toCssString()} : null;
    }

    private _renderOtherUserSelections()
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( !activeUsers.otherUsers )
            return null;

        return this._getOtherUserSelections
            .map(user =>
            {
                return <OtherUserSelection key={user.userId} userId={user.userId} />;
            });
    }

    private _renderDecorator()
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {zoom}}}}} = rootState;
        const {isReactive, visualBox} = this.props;

        const isViewport:boolean = visualBox.box.isViewport;
        const isSelected:boolean = this._isBoxSelected;

        const className:string = isViewport || isSelected
            ? cx('visibleContent')
            : cx('hiddenContent');

        return (
            <BoxDecorator
                isReactive={isReactive}
                className={className}
                box={visualBox.box}
                style={
                    zoom !== 1
                        ? {
                            zoom: Math.max(1 / zoom, 0.5)
                        }
                        : null
                }
            />
        );
    }

    @action
    private _onBoxMouseDown = (event:any) =>
    {
        const {surfaceArea, surfaceDnD} = controllers.ui;
        const {visualBox, repeater} = this.props;

        const {componentDesc} = visualBox.box;
        const isBoxLocked:boolean = componentDesc.lockProp.value.value;

        if( isBoxLocked )
            return;

        if( surfaceArea.isInPreviewOrZenPreviewMode()
            && ((event as MouseEvent).button === 0
                || (event.type === 'touchstart'
                    && (event as TouchEvent).changedTouches
                    && (event as TouchEvent).changedTouches.length === 1)) )
        {
            surfaceDnD.processDragStart(visualBox, repeater, this._uniqueId, event);
        }
    };

    @action
    private _onBoxMouseOver = (event:React.MouseEvent<HTMLDivElement>) =>
    {
        const {surfaceArea} = controllers.ui;
        const {visualBox, repeater} = this.props;

        if( surfaceArea.isInPreviewOrZenPreviewMode() || this._isReparentingInProgress )
            return false;

        event.stopPropagation();

        surfaceArea.setMouseOverRef(new MouseOverRef(visualBox.box, repeater));
    };

    @action
    private _onBoxMouseLeave = (event:any) =>
    {
        const {surfaceArea} = controllers.ui;

        if( surfaceArea.isInPreviewOrZenPreviewMode() || this._isReparentingInProgress )
            return false;

        event.stopPropagation();

        surfaceArea.setMouseOverRef(null);
    };

    @action
    private _onBoxResizeStart = (direction:CompassDir):void | false =>
    {
        const {surfaceArea, surfaceSelection, undoRedo} = controllers.ui;

        undoRedo.disableRecording();

        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        if( !selectedBoxes.length || !surfaceArea.areBoxesResolved(selectedBoxes) )
            return false;

        for( const selectedBox of selectedBoxes )
            if( surfaceArea.isBoxOnMoveByAnotherUser(selectedBox)
                || surfaceArea.isBoxParentOnMoveByAnotherUser(selectedBox)
                || surfaceArea.isBoxChildOnMoveByAnotherUser(selectedBox) )
                return false;

        const bounds = surfaceArea.getBoxesBounds(selectedBoxes);

        surfaceSelection.setSelectionBounds(bounds);
        surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.StartResizing);
        surfaceSelection.setSelectionInteractionDirection(direction);

        this.setState({
            resizeStartX: bounds.x,
            resizeStartY: bounds.y,
            resizeDeltaX: 0,
            resizeDeltaY: 0,
            resizeStartWidth: bounds.width,
            resizeStartHeight: bounds.height,
            resizeDeltaWidth: 0,
            resizeDeltaHeight: 0
        });
    };

    @action
    private _onBoxResizeStop = ():void =>
    {
        const {surfaceSelection, surfaceGuidelines, undoRedo, surfaceArea} = controllers.ui;

        surfaceArea.onBoxResizeStopEvent();

        surfaceGuidelines.stopAndClear();

        undoRedo.enableRecording();

        surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.None);
        surfaceSelection.setSelectionInteractionDirection(null);
    };

    @action
    private _onBoxResizeInProgress = (boundsDelta:SurfaceBounds, direction:CompassDir):void =>
    {
        if( !(boundsDelta.position.x || boundsDelta.position.y || boundsDelta.size.width || boundsDelta.size.height) )
        {
            return;
        }

        const {surfaceArea, surfaceSelection, surfaceGuidelines} = controllers.ui;
        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        for( const selectedBox of selectedBoxes )
            if( surfaceArea.isBoxOnMoveByAnotherUser(selectedBox)
                || surfaceArea.isBoxParentOnMoveByAnotherUser(selectedBox)
                || surfaceArea.isBoxChildOnMoveByAnotherUser(selectedBox) )
                return;

        const currentMode = surfaceSelection.getSelectionInteractionMode();

        if( currentMode !== SelectionInteractionMode.Resizing )
            surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.Resizing);

        surfaceGuidelines.throttledUpdateGuidelines();

        let {
            resizeStartX, resizeStartY,
            resizeDeltaX, resizeDeltaY,
            resizeDeltaWidth, resizeDeltaHeight,
            resizeStartWidth, resizeStartHeight
        } = this.state;

        resizeDeltaX += Math.round(boundsDelta.position.x / zoom);
        resizeDeltaY += Math.round(boundsDelta.position.y / zoom);
        resizeDeltaWidth += Math.round(boundsDelta.size.width / zoom);
        resizeDeltaHeight += Math.round(boundsDelta.size.height / zoom);

        const newSelectionBounds = Bounds.from(
            resizeStartX + resizeDeltaX,
            resizeStartY + resizeDeltaY,
            resizeStartWidth + resizeDeltaWidth,
            resizeStartHeight + resizeDeltaHeight
        );

        surfaceSelection.setSelectionBounds(newSelectionBounds);

        const snapBounds = surfaceGuidelines.getSnapBounds(SelectionInteractionMode.Resizing, direction);

        if( !snapBounds )
            return;

        if( boundsDelta.position.x || boundsDelta.position.y )
            surfaceSelection.setSelectionBounds(snapBounds);
        else
            surfaceSelection.setSelectionBounds(Bounds.from(
                snapBounds.x, snapBounds.y, newSelectionBounds.width, newSelectionBounds.height
            ));

        const {visualBox} = this.props;

        const {layoutLeft, layoutTop, layoutHeight, layoutWidth} = visualBox.box.componentDesc;

        // *******************************************************************

        let parentBounds:ClientRect;
        const box:Box = selectedBoxes[0];

        const rect = surfaceArea.getBoxesBounds(selectedBoxes).toClientRect();
        const snapRect = snapBounds.toClientRect();

        if( newSelectionBounds.x !== snapRect.left )
        {
            const deltaX2 = newSelectionBounds.x - snapRect.left;
            resizeDeltaX -= deltaX2;
            resizeDeltaWidth += deltaX2;
        }

        if( newSelectionBounds.y !== snapRect.top )
        {
            const deltaY2 = newSelectionBounds.y - snapRect.top;
            resizeDeltaY -= deltaY2;
            resizeDeltaHeight += deltaY2;
        }

        const deltaX = snapRect.left - rect.left;
        const deltaY = snapRect.top - rect.top;

        const deltaWidth = snapRect.width - rect.width;
        const deltaHeight = snapRect.height - rect.height;

        // *******************************************************************

        let top:Units;
        let left:Units;
        let width:Units;
        let height:Units;

        if( deltaY !== 0 )
        {
            if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
            {
                if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                if( !parentBounds ) return;

                const deltaTop = (deltaY / parentBounds.height) * 100;

                top = new Units(
                    ANumber.fromNumber(layoutTop.value.value + deltaTop),
                    layoutTop.unitsType
                );
            }
            else
            {
                top = new Units(
                    ANumber.fromNumber(snapBounds.y),
                    layoutTop.unitsType
                );
            }
        }

        if( deltaX !== 0 )
        {
            if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
            {
                if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                if( !parentBounds ) return;

                const deltaLeft = (deltaX / parentBounds.width) * 100;

                left = new Units(
                    ANumber.fromNumber(layoutLeft.value.value + deltaLeft),
                    layoutLeft.unitsType
                );
            }
            else
            {
                left = new Units(
                    ANumber.fromNumber(snapBounds.x),
                    layoutLeft.unitsType
                );
            }
        }

        if( deltaWidth !== 0 )
        {
            if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
            {
                if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                if( !parentBounds ) return;

                const deltaWidthInPercent = (deltaWidth / parentBounds.width) * 100;

                width = new Units(
                    ANumber.fromNumber(layoutWidth.value.value + deltaWidthInPercent),
                    layoutWidth.unitsType
                );
            }
            else
            {
                width = new Units(
                    ANumber.fromNumber(snapBounds.width),
                    layoutWidth.unitsType
                );
            }
        }

        if( deltaHeight !== 0 )
        {
            if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
            {
                if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                if( !parentBounds ) return;

                const deltaHeightInPercent = (deltaHeight / parentBounds.height) * 100;

                height = new Units(
                    ANumber.fromNumber(layoutHeight.value.value + deltaHeightInPercent),
                    layoutHeight.unitsType
                );
            }
            else
            {
                height = new Units(
                    ANumber.fromNumber(snapBounds.height),
                    layoutHeight.unitsType
                );
            }
        }

        if( snapBounds.width >= 0 && snapBounds.height >= 0 )
        {
            const {componentDesc} = visualBox.box;

            top && componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, top);
            left && componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, left);
            width && componentDesc.layoutWidthProp.setPropValue(PropPath.ROOT, width);
            height && componentDesc.layoutHeightProp.setPropValue(PropPath.ROOT, height);

            if( !box.isViewport && (top || left || width || height) )
            {
                if( !parentBounds ) parentBounds = LayoutUtils.getParentHostClientRect(box);
                if( !parentBounds ) return;

                LayoutUtils.resize(direction, componentDesc, parentBounds);
            }
        }

        surfaceArea.onBoxResizeInProgressEvent(visualBox.box);

        this.setState({resizeDeltaX, resizeDeltaY, resizeDeltaWidth, resizeDeltaHeight});
    };

    private get _boxRotationStyle():React.CSSProperties
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        const rotation:RotationModel = repeatedData.get(repeater.index).rotation;

        return rotation ? rotation.toCss() : {};
    }

    @computed
    private get _boxNegRotationStyle():React.CSSProperties | null
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        const rotation:RotationModel = repeatedData.get(repeater.index).rotation;

        if( rotation && rotation.isEnabled.value )
        {
            return {
                transformOrigin: rotation.origin.value.value,
                transform: `rotate(-${rotation.angle.value}deg)`
            };
        }

        return null;
    }

    @computed
    private get _isReparentingInProgress():boolean
    {
        const {surfaceBoxReparenting, surfaceGlass} = controllers.ui;

        const isDraggingComponentFromPanel:boolean = surfaceGlass.isDraggingComponent();
        const isReparentingComponentFromPanel:boolean = surfaceGlass.isReparentingComponent();

        return (
            surfaceBoxReparenting.isBoxReparentingEnabled()
            || isReparentingComponentFromPanel
            || (isDraggingComponentFromPanel && !isReparentingComponentFromPanel)
        );
    }

    @computed
    private get _shouldSetPointerEventsNone():boolean
    {
        const {surfaceArea, surfaceDrawingComponent} = controllers.ui;

        const {visualBox, isInOverlays} = this.props;
        const {componentDesc} = visualBox.box;

        const isInPreviewMode:boolean = surfaceArea.isInPreviewOrZenPreviewMode();
        const isBoxLocked:boolean = componentDesc.lockProp.value.value;
        const isViewport:boolean = visualBox.box.isViewport;
        const isSomeParentLocked:boolean = this._isSomeParentLocked;
        const isDrawingComponentEnabled:boolean = surfaceDrawingComponent.isDrawingComponentEnabled();

        if( this._isDataBoxReparenting )
            return true;

        return (isInOverlays && isInPreviewMode && isViewport)
            || (
                !isInPreviewMode
                && (isViewport || isBoxLocked || (isSomeParentLocked && !this._isOnlyThatBoxSelected))
            )
            || isDrawingComponentEnabled;
    }

    @computed
    private get _isSomeParentLocked():boolean
    {
        const {visualBox} = this.props;

        let parent = visualBox.box.parent;
        let isSomeParentLocked = false;

        while( parent && !isSomeParentLocked )
        {
            if( parent.componentDesc.lockProp.value.value ) isSomeParentLocked = true;
            parent = parent.parent;
        }

        return isSomeParentLocked;
    }

    @computed
    private get _isBoxSelected():boolean
    {
        const {visualBox} = this.props;
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.isBoxSelected(visualBox.box);
    }

    @computed
    private get _areMultipleBoxesSelected():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.areMultipleBoxesSelected();
    }

    @computed
    private get _isSelfDirected():boolean
    {
        const {visualBox, repeater} = this.props;
        const layout:LayoutModel = visualBox.repeatedData.get(repeater.index).layout;

        return PositioningType.isSelfDirected(layout.positioningType);
    }

    private get _scale():ScaleModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).scale;
    }

    private get _opacity():OpacityModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).opacity;
    }

    private get _skew():SkewModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).skew;
    }

    private get _perspective():PerspectiveModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).perspective;
    }

    private get _loader():LoaderModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).loader;
    }

    private _getBounds():SelfAlignedBounds
    {
        const {surfaceArea} = controllers.ui;
        const isInZenPreviewMode:boolean = surfaceArea.isInZenPreviewMode();
        const {visualBox, repeater} = this.props;

        if( this._isSelfDirected )
        {
            return isInZenPreviewMode && visualBox.box.isViewport
                ? {
                    left: '0px',
                    top: '0px',
                    width: '100%',
                    height: '100%'
                }
                : visualBox.selfAlignedBounds.get(repeater.index);
        }
        else
            return visualBox.repeatedData.get(repeater.index).layoutStyle;
    }

    @computed
    private get _isVisible():boolean
    {
        const {visualBox, repeater} = this.props;

        const visibility:ABoolean = visualBox.box.componentDesc.visibilityProp.getRepeatedValue(repeater);

        return !!(visibility && visibility.value);
    }

    @computed
    private get _dndGroupName():string
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).dndGroupName;
    }

    @computed
    private get _isDraggable():boolean
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).isDraggable;
    }

    @computed
    private get _isDropTarget():boolean
    {
        const {visualBox, repeater} = this.props;
        const repeatedData:IRepeatedData = visualBox.repeatedData.get(repeater.index);

        return repeatedData.dnd.isEnabled.value && repeatedData.isDropTarget;
    }

    @computed
    private get _dndTarget():string
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).dndTarget;
    }

    private _getViewportInfoProp():IViewportInfo
    {
        const {isInOverlays, visualBox, isHiddenOverlay} = this.props;
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;
        const {page} = visualBox.box;
        const pageName = page.name;
        const isActive:boolean = page === commonControllers.designState.getActivePage();
        const isInPreviewMode:boolean = controllers.ui.surfaceArea.isInPreviewOrZenPreviewMode();

        return visualBox.box.isViewport
            ? {
                isInOverlays,
                isHiddenOverlay,
                closeOverlayFunction: () => pageName && commonControllers.designState.hideOverlayByName(pageName, true),
                pageId: page.id,
                isActive,
                onResize: commonControllers.designState.onViewportResize,
                isTopmost: isInOverlays && commonControllers.designState.isTheTopmostOverlay(page.id),
                surfaceAreaRef: surfaceAreaDomElement,
                isInDesignMode: !isInPreviewMode
            }
            : null;
    }

    @computed
    private get _isOnlyThatBoxSelected():boolean
    {
        const {area: {selectionState}} = rootState.ui.visualDesigner.surfaceEditor;

        const selectedBoxId:BoxId = selectionState.selectedBoxesOnCurrentLayerPage && selectionState.selectedBoxesOnCurrentLayerPage.length === 1
            ? selectionState.selectedBoxesOnCurrentLayerPage[0].id
            : null;

        return selectedBoxId === this.props.visualBox.box.id;
    }

    @computed
    private get _isDataBoxSelected():boolean
    {
        const {surfaceArea} = controllers.ui;

        const isInPreviewMode:boolean = surfaceArea.isInPreviewOrZenPreviewMode();

        if( isInPreviewMode || this._areMultipleBoxesSelected )
            return false;

        return this._isBoxSelected;
    }

    @computed
    private get _isDataBoxReparenting():boolean
    {
        const {box} = this.props.visualBox;
        const {surfaceBoxReparenting} = controllers.ui;

        return surfaceBoxReparenting.isReparentingBox(box);
    }

    @computed
    private get _isDataMouseOverBox():boolean
    {
        const {box} = this.props.visualBox;
        const {surfaceArea, surfaceSelection, surfaceGlass} = controllers.ui;

        const isInPreviewMode:boolean = surfaceArea.isInPreviewOrZenPreviewMode();
        const isDraggingComponentFromPanel:boolean = surfaceGlass.isDraggingComponent();
        const currentMode:SelectionInteractionMode = surfaceSelection.getSelectionInteractionMode();

        if(
            isInPreviewMode
            || surfaceSelection.isSelectionResizing(currentMode)
            || surfaceSelection.isSelectionMoving(currentMode)
            || isDraggingComponentFromPanel
        )
            return false;

        return surfaceArea.isMouseOverBox(box);
    }

    @computed
    private get _isDataMouseOverDroppableBox():boolean
    {
        const {box} = this.props.visualBox;
        const {surfaceArea, surfaceBoxReparenting} = controllers.ui;

        return surfaceBoxReparenting.isDropIntoBoxAllowed(box) && surfaceArea.isMouseOverBox(box);
    }

    private _isOnReparentingByAnotherUser()
    {
        const {activeUsers} = rootState.ui.visualDesigner;
        const {visualBox} = this.props;

        return !!activeUsers.otherUserBoxReparentingState.get(visualBox.box.id);
    }

    private _isOnMoveByAnotherUser()
    {
        const {surfaceArea} = controllers.ui;
        const {box} = this.props.visualBox;

        return surfaceArea.isBoxOnMoveByAnotherUser(box)
            || surfaceArea.isBoxParentOnMoveByAnotherUser(box)
            || surfaceArea.isBoxChildOnMoveByAnotherUser(box);
    }

    private _addDeltaIfMovedByAnotherUser(bounds:SelfAlignedBounds):SelfAlignedBounds
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        const {visualBox, repeater} = this.props;

        const moveState:IBoxMoveState[] = activeUsers.otherUserBoxMoveState.get(visualBox.box.id);

        if( moveState )
        {
            if( moveState[0].isReparenting )
            {
                let left = new Units(new ANumber(moveState[0].left), new UnitsType(UnitsType.PIXELS)).toCssString();
                let top = new Units(new ANumber(moveState[0].top), new UnitsType(UnitsType.PIXELS)).toCssString();
                const width = new Units(new ANumber(moveState[0].width), new UnitsType(UnitsType.PIXELS)).toCssString();
                const height = new Units(new ANumber(moveState[0].height), new UnitsType(UnitsType.PIXELS)).toCssString();

                return {left, top, width, height};
            }
            else
            {
                const leftUnits = Units.fromUnitsString(new AString(bounds.left));
                let left = new Units(new ANumber(moveState[0].left), leftUnits.unitsType).toCssString();

                const topUnits = Units.fromUnitsString(new AString(bounds.top));
                let top = new Units(new ANumber(moveState[0].top), topUnits.unitsType).toCssString();

                const rigttUnits = Units.fromUnitsString(new AString(bounds.right));
                let right = new Units(new ANumber(moveState[0].right), rigttUnits.unitsType).toCssString();

                const bottomUnits = Units.fromUnitsString(new AString(bounds.bottom));
                let bottom = new Units(new ANumber(moveState[0].bottom), bottomUnits.unitsType).toCssString();

                const widthUnits = Units.fromUnitsString(new AString(bounds.width));
                let width = new Units(new ANumber(moveState[0].width), widthUnits.unitsType).toCssString();

                const heightUnits = Units.fromUnitsString(new AString(bounds.height));
                let height = new Units(new ANumber(moveState[0].height), heightUnits.unitsType).toCssString();

                if( repeater.index )
                {
                    const repeaterModel:RepeaterModel = visualBox.box.componentDesc.repeaterProp.value;

                    if( RepeaterDisplayType.isManual(repeaterModel.displayType) )
                        return {left, top, right, bottom, width, height};

                    let columnsCount = 1;  // vertical

                    if( RepeaterDisplayType.isTiles(repeaterModel.displayType) )
                        columnsCount = repeaterModel.numberOfColumns && repeaterModel.numberOfColumns.value != null
                            ? repeaterModel.numberOfColumns.value
                            : DEFAULT_TILES_NUMBER_OF_COUNT;
                    else if( RepeaterDisplayType.isHorizontal(repeaterModel.displayType) )
                        columnsCount = repeaterModel.count.value;

                    const colIndex = repeater.index % columnsCount;
                    const rowIndex = Math.floor(repeater.index / columnsCount);

                    const leftValue = Units.fromUnitsString(new AString(left)).value.value;
                    const widthValue = Units.fromUnitsString(new AString(width)).value.value;
                    const offsetX = repeaterModel.offsetX ? repeaterModel.offsetX.value.value : 0;

                    left = new Units(new ANumber(leftValue + widthValue * colIndex + offsetX * colIndex), leftUnits.unitsType).toCssString();

                    const topValue = Units.fromUnitsString(new AString(top)).value.value;
                    const heightValue = Units.fromUnitsString(new AString(height)).value.value;
                    const offsetY = repeaterModel.offsetY ? repeaterModel.offsetY.value.value : 0;

                    top = new Units(new ANumber(topValue + heightValue * rowIndex + offsetY * rowIndex), topUnits.unitsType).toCssString();
                }

                return {left, top, right, bottom, width, height};
            }
        }

        return bounds;
    }

    private _fixPositionIfMovedByAnotherUser():'fixed' | 'absolute'
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        const {id} = this.props.visualBox.box;

        const boxMoveState = activeUsers.otherUserBoxMoveState.get(id);

        if( boxMoveState && boxMoveState.length && boxMoveState[0].isReparenting )
            return 'fixed';

        return 'absolute';
    }
}

function shouldComponentUpdateDeepEqual(WrappedComponent)
{
    return class extends BaseComponentWithStateContext<any, any>
    {
        public static readonly childContextTypes = componentStateContextTypes;

        public shouldComponentUpdate(nextProps:IBoxViewProps, nextState:IBoxViewState, nextContext:IComponentStateContext)
        {
            return !ObjectUtils.shallowCompareKeys(this.props, nextProps) ||
                !ObjectUtils.shallowCompareKeys(this.context, nextContext);
        }

        public render():JSX.Element
        {
            return <WrappedComponent {...this.props} />;
        }
    };
}

const BoxViewWrapped = shouldComponentUpdateDeepEqual(BoxView);

export {BoxViewWrapped as BoxView};
